import { apiFetch, apiPost } from './api';
import type { Client, CreateClientPayload, Project, CreateProjectPayload } from '@/types';

// The company list/get endpoints populate projectCount + contractCount on each
// company (contractCount aggregates across all the company's projects). These
// drive the vault folder counts directly, so they no longer have to be derived
// from the progressively-loaded contract set.
export async function getClients(): Promise<Client[]> {
  return apiFetch<Client[]>('companies');
}

export async function getClient(clientUuid: string): Promise<Client> {
  return apiFetch<Client>(`companies/${clientUuid}`);
}

// Note: the create response does NOT populate projectCount/contractCount — they
// arrive null here. A brand-new company has none anyway, so the UI shows 0.
export async function saveClient(client: CreateClientPayload): Promise<Client> {
  return apiPost<Client, CreateClientPayload>('companies', client);
}

export async function getProjects(clientUuid: string): Promise<Project[]> {
  return apiFetch<Project[]>(`companies/${clientUuid}/projects`);
}

/**
 * Every project the caller can access, resolved server-side (GET /api/projects is
 * access-post-filtered). This is how directly-granted projects reach the vault's "Shared with me"
 * section (ADR 0025 / S7): the per-client listing above only covers clients the caller is a
 * member of, so a project granted on its own is invisible to it.
 */
export async function listAccessibleProjects(): Promise<(Project & { clientUuid: string })[]> {
  const dtos = await apiFetch<(Project & { customerId: string })[]>('projects');
  return dtos.map((dto) => ({ ...dto, clientUuid: dto.customerId }));
}

/**
 * Fetches a single project by id, resolving its owning client. Exists for deep links that
 * identify only the project (the URL never carries the client — it is derived from here).
 * The API names the owning company `customerId`; map it onto the client-side `clientUuid`.
 */
export async function getProject(projectUuid: string): Promise<Project & { clientUuid: string }> {
  const dto = await apiFetch<Project & { customerId: string }>(`projects/${projectUuid}`);
  return { ...dto, clientUuid: dto.customerId };
}

export async function saveProject(
  clientUuid: string,
  project: CreateProjectPayload
): Promise<Project> {
  return apiPost<Project, CreateProjectPayload>(`companies/${clientUuid}/projects`, project);
}

export const clientService = {
  getClients,
  getClient,
  saveClient,
  getProjects,
  listAccessibleProjects,
  getProject,
  saveProject,
};
